import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import type { EffectiveAreaResult } from '../../types/apiModels'
import { sortSiteNamesNumerically } from '../../utils/randomize/sortUtils'
import { ManualSitesSection } from './ManualSitesSection'
import { RandomSitesSection } from './RandomSitesSection'
import { StrataAccordionHeader } from './StrataAccordionHeader'

interface StrataFeature {
  properties: {
    id?: string | number
    name: string
    color: string
    area: number
    belongToFarm?: boolean
  }
}

interface MonitoringSiteFeature {
  properties: {
    id?: string | number
    clientId?: string
    name: string
    isRandomSite?: boolean
    newManualSite?: boolean
    randomizeCounter?: number
    hasAssociatedTasks?: boolean
  }
  geometry: { coordinates: [number, number] }
}

interface StrataAccordionProps {
  strata: StrataFeature
  expanded: boolean
  canEditSites: boolean
  wasSaved: boolean
  isViewOnly: boolean
  monitoringSitesBySamplingAreas: Record<string, MonitoringSiteFeature[]>
  effectiveAreas?: EffectiveAreaResult[]
  totalEffectiveArea?: number
  lockReason?: 'zero-area' | 'has-random-sites'
  reRandomize?: boolean
  hasUnsavedSites?: boolean
  disabledTooltip?: string
  checkValue: (name: string) => string
  onExpandedChange: (strataName: string, expanded: boolean) => void
  onSitesChange: (strataName: string, value: string) => void
  onRandomSiteNameChange: (strataName: string, index: number, newName: string) => void
  onManualSiteNameChange: (strataName: string, index: number, newName: string) => void
  onReRandomize: (strataName: string, index: number) => void
  onDeleteManualSite: (strataName: string, index: number) => void
  onAddManualSite: (strata: StrataFeature) => void
}

export const StrataAccordion = ({
  strata,
  expanded,
  canEditSites,
  wasSaved,
  isViewOnly,
  monitoringSitesBySamplingAreas,
  effectiveAreas,
  totalEffectiveArea = 0,
  lockReason,
  reRandomize,
  hasUnsavedSites,
  disabledTooltip,
  checkValue,
  onExpandedChange,
  onSitesChange,
  onRandomSiteNameChange,
  onManualSiteNameChange,
  onReRandomize,
  onDeleteManualSite,
  onAddManualSite,
}: StrataAccordionProps) => {
  const strataName = strata.properties.name
  const allSites = monitoringSitesBySamplingAreas[strataName] || []
  // El orden de los aleatorios solo se ajusta tras guardar. Mientras se edita un nombre,
  // re-ordenar en cada tecla movería la fila del input y haría perder el foco.
  const randomSitesUnsorted = allSites.filter(site => site.properties.isRandomSite)
  const randomSites = wasSaved ? sortSiteNamesNumerically(randomSitesUnsorted) : randomSitesUnsorted
  const manualSites = sortSiteNamesNumerically(
    allSites.filter(site => !site.properties.isRandomSite),
  )
  const matchingEffectiveArea = effectiveAreas?.find(
    ea => ea.samplingAreaId === String(strata.properties.id) || ea.name === strata.properties.name,
  )

  return (
    <Accordion
      expanded={expanded}
      sx={{ width: '100%' }}
      onChange={(_, isExpanded) => onExpandedChange(strataName, isExpanded)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': {
            margin: 0,
          },
          py: 2,
          px: 1,
        }}
      >
        <StrataAccordionHeader
          canEditSites={canEditSites}
          checkValue={checkValue}
          disabledTooltip={disabledTooltip}
          effectiveArea={matchingEffectiveArea}
          hasUnsavedSites={hasUnsavedSites}
          isViewOnly={isViewOnly}
          lockReason={lockReason}
          monitoringSitesBySamplingAreas={monitoringSitesBySamplingAreas}
          strata={strata}
          totalEffectiveArea={totalEffectiveArea}
          onSitesChange={onSitesChange}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 1 }}>
        <RandomSitesSection
          randomSites={randomSites}
          reRandomize={reRandomize}
          strataColor={strata.properties.color}
          wasSaved={wasSaved}
          onNameChange={(index, newName) => onRandomSiteNameChange(strataName, index, newName)}
          onReRandomize={index => onReRandomize(strataName, index)}
        />
        <ManualSitesSection
          isViewOnly={isViewOnly}
          manualSites={manualSites}
          wasSaved={wasSaved}
          onAddManualSite={() => onAddManualSite(strata)}
          onDelete={index => onDeleteManualSite(strataName, index)}
          onNameChange={(index, newName) => onManualSiteNameChange(strataName, index, newName)}
        />
      </AccordionDetails>
    </Accordion>
  )
}
